import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
  IconButton,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { AdminDataContext } from "../../../useContext/AdminContext";
import API from "../../../api";
import moment from "moment";
import CancelIcon from "@mui/icons-material/Cancel";
const ProductDetailsModal = ({ open, onClose, productId }) => {
  const { snackbar } = useContext(AdminDataContext);
  const theme = useTheme();

  // State management
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch product details
  const fetchProductDetails = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await API.get(`/api/v1/products/products/${productId}`);

      if (response.data.success) {
        setProduct(response.data.data.product);
      } else {
        setError(response.data.message || "Failed to fetch product details");
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      setError("Error fetching product details");
      snackbar("Error fetching product details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch product details when modal opens or productId changes
  useEffect(() => {
    if (open && productId) {
      fetchProductDetails();
    }
  }, [open, productId]);

  // Handle refresh
  const handleRefresh = () => {
    fetchProductDetails();
  };

  // Handle delete bundle item (placeholder for future implementation)
  const handleDeleteBundleItem = (itemId) => {
    snackbar("Delete functionality coming soon", "info");
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: `linear-gradient(135deg, ${theme.palette.cream.light} 0%, ${theme.palette.cream.main} 100%)`,
          border: `1px solid ${theme.palette.primary.light}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Typography>Product Details</Typography>
          <IconButton onClick={onClose} aria-label="close" color="inherit">
            <CancelIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : product ? (
          <Box>
            {/* Product Information Section */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: theme.palette.cream.light }}>
              <Typography variant="h5" gutterBottom color="primary">
                {product.name}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {product.description
                  ? product.description.replace(/<[^>]*>/g, "")
                  : "No description available"}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Product ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {product.id}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {product.sku || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Price
                  </Typography>
                  {product.sale_price > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        color="text.secondary"
                        sx={{
                          textDecoration: "line-through",
                          fontSize: "0.875rem",
                        }}
                      >
                        ${product.price?.toFixed(2) || "0.00"}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="success.main"
                        sx={{ fontSize: "1rem" }}
                      >
                        ${product.sale_price?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="h6"
                      color="primary.main"
                      fontWeight="bold"
                    >
                      $
                      {product.calculated_price?.toFixed(2) ||
                        product.price?.toFixed(2) ||
                        "0.00"}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cost Price
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color="info.main"
                  >
                    ${product.cost_price?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Retail Price
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color="info.main"
                  >
                    ${product.retail_price?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Weight
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {product.weight || 0} kg
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Stock
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {(() => {
                      // Calculate dynamic stock based on product type
                      const getDynamicStock = (product) => {
                        // Check if it's a bundle product
                        if (
                          product.bCProductCustomField?.is_bundle &&
                          product.bCProductCustomField?.bundle_items?.length > 0
                        ) {
                          // For bundle products, find the minimum stock among bundle items
                          const bundleStocks =
                            product.bCProductCustomField.bundle_items
                              .map((item) => {
                                // Try different possible field names for inventory level
                                const stock =
                                  item.inventory_level ||
                                  item.stock ||
                                  item.quantity ||
                                  0;
                                return stock;
                              })
                              .filter(
                                (stock) =>
                                  stock !== null &&
                                  stock !== undefined &&
                                  stock > 0
                              );

                          // If no valid stocks found in bundle items, fall back to product's own inventory_level
                          if (bundleStocks.length === 0) {
                            return product.inventory_level || 0;
                          }

                          return Math.min(...bundleStocks);
                        } else {
                          // For regular products, use normal inventory level
                          return product.inventory_level || 0;
                        }
                      };

                      return getDynamicStock(product);
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Sold
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {product.total_sold || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Visible
                  </Typography>
                  <Chip
                    label={product.is_visible ? "Yes" : "No"}
                    color={product.is_visible ? "success" : "error"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Featured
                  </Typography>
                  <Chip
                    label={product.is_featured ? "Yes" : "No"}
                    color={product.is_featured ? "warning" : "default"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Custom Product
                  </Typography>
                  <Chip
                    label={
                      product.bCProductCustomField?.is_custom_product
                        ? "Yes"
                        : "No"
                    }
                    color={
                      product.bCProductCustomField?.is_custom_product
                        ? "info"
                        : "default"
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Product Type
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {product.bCProductCustomField?.is_bundle
                      ? "Bundle"
                      : "Single Product"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {moment(product.date_created).format(
                      "MMMM DD, YYYY [at] h:mm A"
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Bundle Items Table */}
            {product.bCProductCustomField?.is_bundle &&
              Array.isArray(product.bCProductCustomField.bundle_items) &&
              product.bCProductCustomField.bundle_items.length > 0 && (
                <Paper sx={{ p: 3, bgcolor: theme.palette.cream.light }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Bundle Items (
                    {product.bCProductCustomField.bundle_items.length})
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: theme.palette.primary.light,
                            "& .MuiTableCell-head": {
                              color: theme.palette.primary.contrastText,
                              fontWeight: 600,
                            },
                          }}
                        >
                          <TableCell>S.No</TableCell>
                          <TableCell>Product Name</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>BC Price</TableCell>
                          <TableCell>Cost Price</TableCell>
                          <TableCell>Retail Price</TableCell>
                          <TableCell>Weight</TableCell>
                          <TableCell>Stock</TableCell>
                          <TableCell>View Count</TableCell>
                          <TableCell>Is Visible</TableCell>
                          <TableCell>Total Price</TableCell>
                          <TableCell>Data Source</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Bundle Product Header Row */}
                        <TableRow
                          sx={{
                            backgroundColor: theme.palette.cream.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.primary.light + "20",
                            },
                          }}
                        >
                          <TableCell>-</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {product.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.sku || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {product.sale_price > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  color="text.secondary"
                                  sx={{
                                    textDecoration: "line-through",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  ${product.price?.toFixed(2) || "0.00"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color="success.main"
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  ${product.sale_price?.toFixed(2) || "0.00"}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="primary"
                              >
                                ${product.price?.toFixed(2) || "0.00"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color="info.main"
                            >
                              ${product.cost_price?.toFixed(2) || "0.00"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color="info.main"
                            >
                              ${product.retail_price?.toFixed(2) || "0.00"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {product.weight || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {product.inventory_level || 0}
                              {/* {(() => {
                              // Calculate dynamic stock based on product type
                              const getDynamicStock = (product) => {
                                // Check if it's a bundle product
                                if (product.bCProductCustomField?.is_bundle && product.bCProductCustomField?.bundle_items?.length > 0) {
                                  // For bundle products, find the minimum stock among bundle items
                                  const bundleStocks = product.bCProductCustomField.bundle_items
                                    .map(item => {
                                      // Try different possible field names for inventory level
                                      const stock = item.inventory_level || item.stock || item.quantity || 0;
                                      return stock;
                                    })
                                    .filter(stock => stock !== null && stock !== undefined && stock > 0);
                                  
                                  // If no valid stocks found in bundle items, fall back to product's own inventory_level
                                  if (bundleStocks.length === 0) {
                                    return product.inventory_level || 0;
                                  }
                                  
                                  return Math.min(...bundleStocks);
                                } else {
                                  // For regular products, use normal inventory level
                                  return product.inventory_level || 0;
                                }
                              };
                              
                              return getDynamicStock(product);
                            })()} */}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {product.view_count || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={product.is_visible ? "Yes" : "No"}
                              color={product.is_visible ? "success" : "error"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color="primary"
                            >
                              ${product.calculated_price?.toFixed(2) || "0.00"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                product.bCProductCustomField
                                  ? "App Product"
                                  : "BC Product"
                              }
                              color={
                                product.bCProductCustomField
                                  ? "primary"
                                  : "secondary"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<RefreshIcon />}
                              onClick={handleRefresh}
                              sx={{ minWidth: "auto", p: 0.5 }}
                            >
                              Refresh
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* Bundle Items Rows */}
                        {product.bCProductCustomField.bundle_items.map(
                          (item, index) => (
                            <TableRow
                              key={item.id || item._id}
                              sx={{
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.primary.light + "20",
                                },
                                "&:nth-of-type(even)": {
                                  backgroundColor: theme.palette.cream.main,
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {index + 1}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {item.name || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {item.sku || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {item.sale_price > 0 ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                      color="text.secondary"
                                      sx={{
                                        textDecoration: "line-through",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      $
                                      {item.price?.toFixed(2) ||
                                        item.calculated_price?.toFixed(2) ||
                                        "0.00"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      color="success.main"
                                      sx={{ fontSize: "0.875rem" }}
                                    >
                                      ${item.sale_price?.toFixed(2) || "0.00"}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="primary"
                                  >
                                    $
                                    {item.price?.toFixed(2) ||
                                      item.calculated_price?.toFixed(2) ||
                                      "0.00"}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  color="info.main"
                                >
                                  ${item.cost_price?.toFixed(2) || "0.00"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  color="info.main"
                                >
                                  ${item.retail_price?.toFixed(2) || "0.00"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {item.weight || 0}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {item.inventory_level || 0}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {item.view_count || 0}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.is_visible ? "Yes" : "No"}
                                  color={item.is_visible ? "success" : "error"}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  color="primary"
                                >
                                  $
                                  {item.calculated_price?.toFixed(2) ||
                                    item.price?.toFixed(2) ||
                                    "0.00"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    item.bCProductCustomField
                                      ? "App Product"
                                      : "BC Product"
                                  }
                                  color={
                                    item.bCProductCustomField
                                      ? "primary"
                                      : "secondary"
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {/* <Button
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteBundleItem(item.id)}
                              color="error"
                              sx={{ minWidth: 'auto', p: 0.5 }}
                            >
                              Delete
                            </Button> */}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
          </Box>
        ) : null}
      </DialogContent>

      {/* <DialogActions sx={{ p: 3, backgroundColor: theme.palette.cream.main }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
       
      </DialogActions> */}
    </Dialog>
  );
};

export default ProductDetailsModal;
